import { EventStreamContentType, fetchEventSource } from "@microsoft/fetch-event-source";
import { Observable } from "rxjs";

/** Activity from Cortana SSE streams (see `BaseFindSseHub` + `postJsonSseObservable`). */
export type SseActivityDetail =
  | { kind: "open"; connectionId?: string }
  | { kind: "heartbeat" }
  | { kind: "payload" }
  | { kind: "disconnected" };

const INITIAL_RETRY_DELAY_MS = 1_000;
const MAX_RETRY_DELAY_MS = 10_000;

/**
 * When `publicRuntimeConfig.cortanaBaseUrl` is set, use it so the browser talks to Cortana directly
 * (`https://…/api/.../sse`). That avoids the master-chief Nuxt proxy hop where long-lived chunked SSE
 * over HTTP/2 often surfaces as `net::ERR_HTTP2_PROTOCOL_ERROR` (200 OK) in Chrome.
 */
function resolveSseUrl(relativePathWithQuery: string): string {
  if (typeof window === "undefined") {
    return relativePathWithQuery;
  }
  const base = (window as { $nuxt?: { $config?: { cortanaBaseUrl?: string } } }).$nuxt?.$config?.cortanaBaseUrl?.trim();

  if (!base) {
    return relativePathWithQuery;
  }
  const path = relativePathWithQuery.startsWith("/")
    ? relativePathWithQuery
    : `/${relativePathWithQuery}`;

  return `${base.replace(/\/$/, "")}${path}`;
}

function readConnectionId(response: Response): string | undefined {
  const lower = response.headers.get("x-sse-connection-id");

  if (lower) {
    return lower;
  }

  return response.headers.get("X-Sse-Connection-Id") ?? undefined;
}

function nextRetryDelay(prev: number): number {
  return Math.min(prev * 2, MAX_RETRY_DELAY_MS);
}

/**
 * POST JSON to a Cortana `text/event-stream` endpoint (direct to Cortana when `cortanaBaseUrl` is set, else proxied).
 * Emits mapped payloads only for non-empty `data:` frames.
 * Empty `data` dispatches (e.g. `:keepalive\\n\\n` heartbeats) report `kind: heartbeat`.
 * Subscribers should unsubscribe to abort — required so the hub unregisters the connection.
 *
 * Auto-reconnects on transient network errors and unexpected stream closes (server-side close mid-stream),
 * with exponential backoff capped at MAX_RETRY_DELAY_MS. Reconnection is suppressed once the consumer
 * unsubscribes (the AbortController fires) so explicit teardown never resurrects the connection.
 */
export function postJsonSseObservable<T>(
  url: string,
  body: unknown,
  mapPayload: (rawJson: string) => T,
  onActivity?: (detail: SseActivityDetail) => void
): Observable<T> {
  return new Observable<T>((subscriber) => {
    const abortController = new AbortController();
    const sseUrl = resolveSseUrl(url);
    let retryDelay = INITIAL_RETRY_DELAY_MS;

    fetchEventSource(sseUrl, {
      method: "POST",
      body: JSON.stringify(body ?? {}),
      signal: abortController.signal,
      openWhenHidden: true,
      fetch: async(input, init): Promise<Response> => {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const token = await (window as any).$nuxt?.$fire?.auth?.currentUser?.getIdToken();
        const headers = new Headers(init?.headers);

        headers.set("Content-Type", "application/json");

        if (token) {
          headers.set("Cortana-Auth", token);
        }
        return fetch(input, { ...init, headers, mode: "cors" });
      },
      onopen: (response): Promise<void> => {
        const ct = response.headers.get("content-type");

        if (!ct?.startsWith(EventStreamContentType)) {
          return Promise.reject(new Error(`Expected content-type to be ${EventStreamContentType}, actual: ${ct}`));
        }
        retryDelay = INITIAL_RETRY_DELAY_MS;
        onActivity?.({ kind: "open", connectionId: readConnectionId(response) });
        return Promise.resolve();
      },
      onmessage: (msg): void => {
        const raw = msg.data?.trim?.() ?? "";

        if (!raw.length) {
          onActivity?.({ kind: "heartbeat" });
          return;
        }

        onActivity?.({ kind: "payload" });

        try {
          subscriber.next(mapPayload(raw));
        } catch (e) {
          subscriber.error(e);
        }
      },
      onclose: (): void => {
        // Server cleanly ended the stream. Throw so fetchEventSource routes through onerror → retry.
        onActivity?.({ kind: "disconnected" });
        throw new Error("SSE stream closed by server");
      },
      onerror: (err): number => {
        if (abortController.signal.aborted) {
          // Consumer unsubscribed — surface the error so fetchEventSource exits its retry loop.
          throw err;
        }
        onActivity?.({ kind: "disconnected" });
        const delay = retryDelay;

        retryDelay = nextRetryDelay(retryDelay);
        return delay;
      }
    }).catch((err) => {
      if (!abortController.signal.aborted) {
        subscriber.error(err);
      }
    });

    return (): void => abortController.abort();
  });
}
